import React, { useState, useEffect, useMemo } from "react"; 
import { 
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, 
  DialogActions, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Card, CardContent, 
  Grid, Tooltip, TextField, InputAdornment, TableFooter, TablePagination,
  MenuItem, FormControl, InputLabel, Select
} from "@mui/material"; 
import { 
  Close, Person, Payment, Receipt, AccountBalance, CheckCircle, Warning,
  AttachMoney, TrendingUp, AccountBalanceWallet, Search, FilterList
} from "@mui/icons-material"; 
import { DatePicker } from "@mui/x-date-pickers"; 
import { formatCurrencyN } from "../../../common/utils_t"; 
import { confirmTransactionAPI, getCustomersOrders_conf_giftcard_crypto_all } from "../../../api/customers/customers"; 
import { getExternalProcessorsAPI } from "../../../api/orders/orders";
import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, useIonViewWillEnter } from "@ionic/react"; 
import { MenuButton } from "../../../components/Menu";

interface ExternalProcessor {
  id: string;
  name: string;
  type: string;
  category: string;
}

interface OrderItem { 
  product_id: string; 
  quantity: string; 
  product_name: string; 
  price: string; 
  total_amount: string; 
  item_status: string; 
  card_amount?: string | null; 
  load_type?: string | null; 
  card_rate?: string | null; 
  naira_rate?: string | null; 
  internal_rate?: string | null;
  username?: string | null; 
  processor_type?: string | null; 
  external_processor?: string | null;
  customer_bank_code?: string | null; 
  customer_account_number?: string | null; 
  customer_account_name?: string | null;
  otp_status: string | null; 
  last_updated: string;
  deleted_at?: string | null; 
  created_at?: string | null; 
  updated_at?: string | null;
  product_id_type: string; 
  product_id_single: string; 
  rmbValue: string; 
  commission: string; 
  proof_of_payment: string; 
  costPrice: string; 
  sellingPrice: string; 
}

interface Transaction { 
  product_name: string; 
  order_status: string; 
  transactionStartTime: React.ReactNode; 
  id: number;
  business_id: string; 
  branch_id: string | null; 
  payout_status: 1 | 0;
  order_id: string; 
  order_code: string; 
  order_date: string;
  active_status: "COMPLETED" | "PENDING" | "CANCELLED" | "REVERSED";
  payment_status: 0 | 1 | 2;
  total_price: string; 
  payable_amount?: string | null; 
  amount_paid?: string | null; 
  balance_due?: number;
  mode_of_payment: string | null; 
  order_by: string; 
  approved_by?: string | null;
  member_id: string; 
  member_name: string;
  customer_name?: string | null; 
  customer_email?: string | null; 
  customer_phone?: string | null;
  request_id?: string | null; 
  card_amount?: string | null; 
  load_type?: string | null; 
  card_rate?: string | null; 
  naira_rate?: string | null; 
  internal_rate?: string | null;
  username?: string | null; 
  processor_type?: string | null; 
  external_processor?: string | null;
  processor_category?: string | null; 
  customer_bank_code?: string | null; 
  customer_account_number?: string | null; 
  customer_account_name?: string | null;
  otp_status: string | null; 
  last_updated: string;
  deleted_at?: string | null; 
  created_at?: string | null; 
  updated_at?: string | null;
  product_id_type: string; 
  product_id_single: string; 
  rmbValue: string; 
  commission: string; 
  proof_of_payment: string; 
  costPrice: string; 
  sellingPrice: string; 
  cryptoType?: string | null; 
  cryptoAmount?: string | null; 
  product_face_code?: string | null;
  is_verified?: number;
  new_nairaRate?: string | null;
  new_costPrice?: string | null;
  new_commission?: string | null;
  payout_date?: string | null;
  items: OrderItem[]; 
  staff_name?: string | null;
  cardUnit?: number;
  cardValue?: number;
  unitPrice?: string;
  note2?: string;
}

const CustomerAccountPage_conf_giftcard_crypto_all: React.FC = () => { 
  const [transactions, setTransactions] = useState<Transaction[]>([]); 
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState({ transactions: false }); 
  const [totalCardAmount, setTotalCardAmount] = useState(0); 
  const [totalCryptoAmount, setTotalCryptoAmount] = useState(0); 

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [externalProcessorFilter, setExternalProcessorFilter] = useState("All");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Verification states
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [transactionToVerify, setTransactionToVerify] = useState<Transaction | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Processor states
  const [processors, setProcessors] = useState<ExternalProcessor[]>([]);
  const [usdProcessors, setUsdProcessors] = useState<ExternalProcessor[]>([]);

  // Check if any transaction has CHINESE processor category
  const hasChineseTransactions = filteredTransactions.some(
    txn => txn.processor_category === 'CHINESE'
  );

  // Calculate totals for filtered data
  const calculateFilteredTotals = (txns: Transaction[]) => {
    const cardAmount = txns.reduce((sum, txn) => sum + parseFloat(txn.card_amount || "0"), 0);
    const sellingPrice = txns.reduce((sum, txn) => sum + parseFloat(txn.sellingPrice || "0"), 0);
    const commission = txns.reduce((sum, txn) => sum + parseFloat(txn.commission || "0"), 0);
    const newCommission = txns.reduce((sum, txn) => sum + parseFloat(txn.new_commission || "0"), 0);
    const costPrice = txns.reduce((sum, txn) => sum + parseFloat(txn.costPrice || "0"), 0);
    const newCostPrice = txns.reduce((sum, txn) => sum + parseFloat(txn.new_costPrice || "0"), 0);

    return {
      cardAmount,
      sellingPrice,
      commission,
      newCommission,
      costPrice,
      newCostPrice
    };
  };

  const filteredTotals = calculateFilteredTotals(filteredTransactions);

  useEffect(() => { 
    const cardAmount = transactions.reduce( 
      (sum, txn) => sum + parseFloat(txn.card_amount || "0"), 
      0 
    ); 
    setTotalCardAmount(cardAmount); 

    const cryptoAmount = transactions.reduce( 
      (sum, txn) => sum + parseFloat(txn.cryptoAmount || "0"), 
      0 
    ); 
    setTotalCryptoAmount(cryptoAmount); 
  }, [transactions]); 

  const fetchExternalProcessors = async () => {
    try {
      const response = await getExternalProcessorsAPI();
      console.log("External processors response:", response);
      
      let processorsData: ExternalProcessor[] = [];
      if (response.data && Array.isArray(response.data)) {
        processorsData = response.data;
      } else if (Array.isArray(response)) {
        processorsData = response;
      } else {
        console.warn("Unexpected response format from getExternalProcessorsAPI:", response);
        processorsData = [];
      }
      
      setProcessors(processorsData);
      
      const usdProcessorsData = processorsData.filter(processor => 
        processor.type === 'USD' || processor.category === 'USD'
      );
      setUsdProcessors(usdProcessorsData);
      
    } catch (error) {
      console.error("Error fetching external processors:", error);
      setProcessors([]);
      setUsdProcessors([]);
    }
  };

  const allProcessors = useMemo(() => {
    const apiProcessors = processors || [];
    const transactionProcessors = new Set(apiProcessors.map(p => p.name));
    
    const uniqueTransactionProcessors: ExternalProcessor[] = [];
    
    transactions.forEach(txn => {
      if (txn.external_processor && !transactionProcessors.has(txn.external_processor)) {
        transactionProcessors.add(txn.external_processor);
        uniqueTransactionProcessors.push({
          id: `generated-${txn.external_processor}`,
          name: txn.external_processor,
          type: 'UNKNOWN',
          category: 'UNKNOWN'
        });
      }
    });
    
    return [...apiProcessors, ...uniqueTransactionProcessors].sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [processors, transactions]);

  // Filter transactions based on search criteria
  useEffect(() => {
    let filtered = transactions;

    // Filter by search term (customer name)
    if (searchTerm) {
      filtered = filtered.filter(txn => 
        txn.member_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate) {
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.transactionStartTime as string);
        const filterDate = new Date(startDate);
        return txnDate >= filterDate;
      });
    }

    if (endDate) {
      filtered = filtered.filter(txn => {
        const txnDate = new Date(txn.transactionStartTime as string);
        const filterDate = new Date(endDate);
        filterDate.setHours(23, 59, 59, 999); // End of day
        return txnDate <= filterDate;
      });
    }

    // Filter by external processor
    if (externalProcessorFilter && externalProcessorFilter !== "All") {
      filtered = filtered.filter(txn => 
        txn.external_processor === externalProcessorFilter
      );
    }

    setFilteredTransactions(filtered);
    setPage(0); // Reset to first page when filters change
  }, [transactions, searchTerm, startDate, endDate, externalProcessorFilter]);

  const unpaidTransactions = transactions.filter( 
    (txn) => txn.payment_status === 0 || txn.payment_status === 2 
  ); 
  
  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => { 
    const amount = parseFloat(txn.total_price); 
    const balanceDue = txn.balance_due || amount; 
    return sum + balanceDue; 
  }, 0); 

  const getPaymentStatusLabel = (status: string | number) => { 
    const code = Number(status); 
    switch (code) { 
      case 0: return "Unpaid"; 
      case 1: return "Paid"; 
      case 2: return "Partial"; 
      default: return "Unknown"; 
    } 
  }; 

  const getPaymentStatusColor = (status: string | number) => { 
    switch (Number(status)) { 
      case 0: return "error"; 
      case 1: return "success"; 
      case 2: return "warning"; 
      default: return "default"; 
    } 
  }; 

  const fetchCustomerTransactions_all = async () => { 
    try { 
      setLoading((prev) => ({ ...prev, transactions: true })); 
      const response = await getCustomersOrders_conf_giftcard_crypto_all(); 
     
      console.log("Fetched Transactions:", response); 

      const processedTransactions = response.data.orders.map( 
        (txn: Transaction) => { 
          const totalAmount = parseFloat(txn.payable_amount || txn.total_price); 
          let balanceDue = Number(txn.balance_due); 

          if (txn.payment_status === 2) { 
            balanceDue = totalAmount * 0.5;
          } else if (txn.payment_status === 1) { 
            balanceDue = 0; 
          } 

          return { 
            ...txn, 
            balance_due: balanceDue, 
          }; 
        } 
      ); 
      setTransactions(processedTransactions); 
      setFilteredTransactions(processedTransactions);
      setLoading((prev) => ({ ...prev, transactions: false }));
    } catch (error) { 
      console.error("Error fetching transactions:", error); 
      setLoading((prev) => ({ ...prev, transactions: false }));
    }
  };

  useIonViewWillEnter(() => {
    fetchCustomerTransactions_all();
    fetchExternalProcessors();
  });

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setStartDate(null);
    setEndDate(null);
    setExternalProcessorFilter("All");
  };

  // Verification handlers
  const handleVerifyClick = (transaction: Transaction) => {
    setTransactionToVerify(transaction);
    setVerifyDialogOpen(true);
  };

  const handleVerifyTransaction = async () => {
    if (!transactionToVerify) return;
    console.log("Starting verification for transaction:", transactionToVerify);
    setIsVerifying(true);
    try {
      const response = await confirmTransactionAPI(transactionToVerify.order_id);
      console.log("Verifying transaction Code:", transactionToVerify.order_code);
      console.log("Verifying transaction ID:", transactionToVerify.order_id);
      console.log("Verifycc API response:", response);

      setIsVerifying(false);
      setVerifyDialogOpen(false);
      fetchCustomerTransactions_all(); // Refresh data
    } catch (error) {
      console.error("Error verifying transaction:", error);
      setIsVerifying(false);
    }
  };

  const totalPayableAmount = transactions.reduce( 
    (sum, txn) => sum + parseFloat(txn.payable_amount || txn.total_price || "0"), 
    0 
  ); 

  // Verification Dialog Component
  const VerificationDialog = () => {
    if (!transactionToVerify) return null;

    const txn = transactionToVerify;
    const isChineseProcessor = txn.processor_category === 'CHINESE';

    return (
      <Dialog open={verifyDialogOpen} onClose={() => setVerifyDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircle sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Confirm PayOut Transaction - {txn.order_id}
            </Typography>
          </Box>
          <IconButton onClick={() => setVerifyDialogOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          {/* Header Summary */}
          <Card sx={{ mb: 3, bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                 <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
  {txn.product_id_type} - 
  {txn.product_id_type === 'GiftCard'
    ? (txn as any).giftcardType
    : txn.cryptoType || ''
  } Transaction 
</Typography>

                  <Typography variant="body2">
                    Date: {txn.transactionStartTime}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Customer: {txn.member_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
                   <Typography variant="body2">Card Amount:
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  { (parseFloat(txn.card_amount || '0'))}
                  </Typography>
                   </Typography>
                
                  <Box sx={{ display: 'flex', justifyContent: { md: 'flex-end' }, gap: 1, mt: 1, flexWrap: 'wrap' }}>
                   
                    <Chip 
                      label={txn.is_confirmed === 1 ? "CONFIRMED" : "PENDING CONFIRMATION"} 
                      color={txn.is_confirmed === 1 ? "success" : "warning"}
                      sx={{ fontWeight: 'bold', color: 'white' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            {/* Transaction Details */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Receipt sx={{ mr: 1, color: 'primary.main' }} />
                    Transaction Details
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <DetailRow label="Order Code" value={txn.order_code} />
                    <DetailRow label="Staff Name" value={txn.staff_name} />
                    <DetailRow label="Customer" value={txn.member_name} />
                    <DetailRow label="Transaction Date" value={txn.transactionStartTime} />
                 <DetailRow
  label="Gift Card Code"
  sx={{
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: 200,
  }}
  value={
    txn.product_face_code
      ? txn.product_face_code.split(',').join('\n')
      : "N/A"
  }
/>

                    <DetailRow label="Product Type" value={txn.product_id_type} />
                
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Card Information */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <Payment sx={{ mr: 1, color: 'primary.main' }} />
                    Card Information
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <DetailRow 
                      label="Card Amount" 
                      value={(parseFloat(txn.card_amount || '0'))} 
                      valueColor="success.main"
                      boldValue
                    />
                                        <DetailRow label="Card Unit" value={txn.cardUnit} />

                    <DetailRow label="Card Value" value={txn.cardValue} />

                    <DetailRow label="Unit Price" value={formatCurrencyN(parseFloat(txn.unitPrice || '0'))} />

                    <DetailRow label="Card Rate" value={formatCurrencyN(parseFloat(txn.card_rate || '0'))} />
                    
                    {/* Conditionally show RMB Value only for CHINESE processors */}
                    {isChineseProcessor && (
<DetailRow
  label="RMB Value"
  value={
    txn.product_id_type === "GiftCard"
      ? Number(txn.rmbValue || 0) * Number(txn.cardUnit || 0)
      : txn.rmbValue
  }
/>
                    )}
                    
                    <DetailRow label="Load Type" value={txn.load_type || "N/A"} />
                    <DetailRow label="Processor / Type " value={`${txn.external_processor} / ${txn.processor_type}`} />

                       <DetailRow label="Processor Category" value={` ${txn.processor_category}`} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Sale Information */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
                    Sale Information
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    {/* Conditionally show Sale Naira Rate only for CHINESE processors */}
                    {isChineseProcessor && (
                      <DetailRow 
                        label="Sale Naira Rate" 
                        value={formatCurrencyN(parseFloat(txn.naira_rate || '0'))}
                      />
                    )}
                    
                    <DetailRow 
                      label="Sale Cost Price" 
                      value={formatCurrencyN(parseFloat(txn.costPrice || '0'))}
                    />      
                    <DetailRow 
                      label="Sale Commission" 
                      value={formatCurrencyN(parseFloat(txn.commission || '0'))}
                      valueColor="info.main"
                    />
                    <DetailRow 
                      label="Selling Price" 
                      value={formatCurrencyN(parseFloat(txn.sellingPrice || '0'))}
                      valueColor="success.main"
                      boldValue
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Payout Information */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <AccountBalanceWallet sx={{ mr: 1, color: 'primary.main' }} />
                    Payout Information
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                  {txn.processor_category === 'CHINESE' && (
                    <DetailRow 
                      label="Payout Rate" 
                      value={formatCurrencyN(parseFloat(txn.new_nairaRate || '0'))}
                    />
                  )}

                    <DetailRow 
                      label="Payout Cost Price" 
                      value={formatCurrencyN(parseFloat(txn.new_costPrice || '0'))}
                    />
                    {txn.processor_category === 'CHINESE' && (
                    <DetailRow 
                      label="Payout Commission" 
                      value={formatCurrencyN(parseFloat(txn.new_commission || '0'))}
                      valueColor="info.main"
                    />
                    )}  
                    <DetailRow label="Payout Date" value={txn.payout_date || "Not Set"} />
                    <DetailRow label="Payout Status" value={
                      <Chip 
                        label={txn.payout_status === 1 ? "Paid Out" : "Pending"} 
                        color={txn.payout_status === 1 ? "success" : "warning"} 
                        size="small" 
                      />
                    } />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Button 
            onClick={() => setVerifyDialogOpen(false)} 
            variant="outlined"
            startIcon={<Close />}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyTransaction}
            variant="contained"
            color="primary"
            disabled={isVerifying || txn.is_confirmed === 1}
            startIcon={isVerifying ? <CircularProgress size={16} /> : <CheckCircle />}
            sx={{ minWidth: 140 }}
          >
            {isVerifying ? "Confirming..." : txn.is_confirmed === 1 ? "Already Confirmed" : "Confirm Transaction"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
    
  // Helper component for detail rows
  const DetailRow: React.FC<{
    label: string;
    value: React.ReactNode;
    valueColor?: string;
    boldValue?: boolean;
    sx?: any;
  }> = ({ label, value, valueColor, boldValue, sx }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 'medium' }}>
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: boldValue ? 'bold' : 'medium', 
          textAlign: 'right', 
          flex: 1,
          color: valueColor || 'text.primary',
          ...sx
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return ( 
    <IonPage className="pushDown"> 

      <IonHeader>
        <IonToolbar>
          <MenuButton/>
          <IonTitle>Audit Processor Payout</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent> 
        <Box sx={{ p: 2 }}> 
          {/* Balance and Payment Summary */} 
          <Grid container spacing={2} sx={{ mb: 3 }}> 
            <Grid item xs={12} sm={6} md={4}> 
              <Card> 
                <CardContent sx={{ textAlign: "center" }}> 
                  <Receipt sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} /> 
                  <Typography variant="h6">Total Value</Typography> 
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}> 
                    {formatCurrencyN(totalPayableAmount)} 
                  </Typography> 
                </CardContent> 
              </Card> 
            </Grid> 
            <Grid item xs={12} sm={6} md={4}> 
              <Card sx={{ bgcolor: "warning.light" }}> 
                <CardContent sx={{ textAlign: "center" }}> 
                  <Warning sx={{ fontSize: 40, mb: 1, color: 'warning.main' }} /> 
                  <Typography variant="h6">Outstanding</Typography> 
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}> 
                    {formatCurrencyN(totalOutstanding)} 
                  </Typography> 
                </CardContent> 
              </Card> 
            </Grid> 
            <Grid item xs={12} sm={6} md={4}> 
              <Card> 
                <CardContent sx={{ textAlign: "center" }}> 
                  <Payment sx={{ fontSize: 40, mb: 1, color: 'success.main' }} /> 
                  <Typography variant="h6">Total Card Amount</Typography> 
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}> 
                    {(totalCardAmount)} 
                  </Typography> 
                </CardContent> 
              </Card> 
            </Grid> 
          </Grid> 

          {/* Search and Filter Section */}
          <Card sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
              <FilterList sx={{ mr: 1 }} />
              Search & Filter
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search by Customer Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {/* Updated Date Pickers */}
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>External Processor</InputLabel>
                  <Select
                    value={externalProcessorFilter}
                    label="External Processor"
                    onChange={(e) => setExternalProcessorFilter(e.target.value)}
                  >
                    <MenuItem value="All">All Processors</MenuItem>
                    {allProcessors?.map((processor) => (
                      <MenuItem key={processor.id} value={processor.name}>
                        {processor.name} {processor.type === 'USD' ? '(USD)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  onClick={handleResetFilters}
                  sx={{ height: '56px' }}
                >
                  Reset Filters
                </Button>
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing {filteredTransactions.length} of {transactions.length} transactions
              {externalProcessorFilter && ` • Filtered by: ${externalProcessorFilter}`}
            </Typography>
          </Card>

          {/* Orders Table */} 
          <Typography variant="h5" gutterBottom sx={{ mt: 4, fontWeight: 'bold', color: 'primary.main' }}> 
            Transaction History 
          </Typography> 

          {loading.transactions ? ( 
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}> 
              <CircularProgress /> 
            </Box> 
          ) : filteredTransactions.length > 0 ? ( 
            <TableContainer component={Paper} elevation={3}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: 'primary.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell> 
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Staff Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell> 
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Card Amount</TableCell> 
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Card Rate</TableCell> 
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}> Card Unit   </TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}> Card Value   </TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}> Unit Price  </TableCell>
                    
                    {/* Conditionally show RMB Value column only for CHINESE processors */}
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>RMB Value</TableCell> 
                    )}
                    
                    {/* Conditionally show Sale Naira Rate column only for CHINESE processors */}
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Sale Naira Rate</TableCell> 
                    )}
                    
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>PayOut Naira Rate</TableCell>
                    )}
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>PayOut Cost Price</TableCell>
                    )}
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Sale Cost Price</TableCell> 
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Sold Price</TableCell>

                    {!hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Selling Price</TableCell>
                    )}
                 
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Sale Commission</TableCell> 
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>PayOut Commission</TableCell>
                    )}

                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Gift Card Code</TableCell> 
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PayOut Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>  Processor</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>OM. Notes</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell> 
                  </TableRow> 
                </TableHead> 
                <TableBody> 
                  {filteredTransactions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((txn) => { 
                      const isChineseProcessor = txn.processor_category === 'CHINESE';
                      
                      return (
                      <TableRow 
                        key={txn.order_id}
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      > 
                        <TableCell>{txn.transactionStartTime}</TableCell> 
                        <TableCell>{txn.staff_name || "N/A"}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Person sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                            {txn.member_name}
                          </Box>
                        </TableCell> 
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            { (parseFloat(txn.card_amount || '0'))}
                          </Typography>
                        </TableCell> 
                        <TableCell align="right">{txn.card_rate}</TableCell> 
                        <TableCell align="right">{txn.cardUnit}</TableCell>
                        <TableCell align="right">{txn.cardValue}</TableCell>
                        <TableCell align="right">{formatCurrencyN(parseFloat(txn.unitPrice || '0'))}</TableCell>

                        {/* Conditionally show RMB Value cell only for CHINESE processors */}
                        {hasChineseTransactions && (
                        <TableCell align="right">
                          {isChineseProcessor
                            ? Number(parseFloat(txn.rmbValue || '0') * (txn.cardUnit || 0)).toLocaleString()
                            : '-'}
                        </TableCell>
                        )}
                        
                        {/* Conditionally show Sale Naira Rate cell only for CHINESE processors */}
                        {hasChineseTransactions && (
                          <TableCell align="right">
                            {isChineseProcessor ? formatCurrencyN(parseFloat(txn.naira_rate || '0')) : '-'}
                          </TableCell> 
                        )}
                        {hasChineseTransactions && (
                          <TableCell align="right">
                            {isChineseProcessor ? formatCurrencyN(parseFloat(txn.new_nairaRate || '0')) : '-'}
                          </TableCell> 
                        )}
                        <TableCell align="right">{formatCurrencyN(parseFloat(txn.new_costPrice || '0'))}</TableCell> 
                        <TableCell align="right">{formatCurrencyN(parseFloat(txn.costPrice || '0'))}</TableCell> 
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrencyN(parseFloat(txn.sellingPrice || '0'))}
                          </Typography>
                        </TableCell> 
                        <TableCell align="right">{formatCurrencyN(parseFloat(txn.commission || '0'))}</TableCell> 
                        {hasChineseTransactions && (
                          txn.processor_type === "INTERNAL" ? (
                            <TableCell align="right">
                              {formatCurrencyN(parseFloat(txn.new_commission || "0"))}
                            </TableCell>
                          ) : (
                            <TableCell align="right">
                              {formatCurrencyN(parseFloat(txn.new_commission || "0"))}
                            </TableCell>
                          )
                        )}
                        <TableCell>
                          {txn.product_face_code ? txn.product_face_code.split(',').join('\n') : "N/A"}
                        </TableCell> 
                        <TableCell>{txn.payout_date || "-"}</TableCell>
                        <TableCell>
                          <Chip 
                            label={txn.external_processor + ' ' + txn.processor_category || "N/A"} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={txn.note2 || "Nill"} 
                            size="small" 
                            variant="outlined"
                            color="secondary"
                          />
                        </TableCell> 
                        <TableCell> 
                          <Tooltip title={txn.is_confirmed === 1 ? "Transaction Confirmed" : "Confirm this transaction"}>
                            <Button 
                              variant="contained" 
                              size="small" 
                              color={txn.is_confirmed === 1 ? "success" : "primary"} 
                              onClick={() => handleVerifyClick(txn)} 
                              startIcon={txn.is_confirmed === 1 ? <CheckCircle /> : <Warning />}
                              sx={{ 
                                minWidth: 100,
                                boxShadow: 1
                              }}
                            > 
                              {txn.is_confirmed === 1 ? "Confirmed" : "Confirm"} 
                            </Button> 
                          </Tooltip>
                        </TableCell> 
                      </TableRow> 
                    )})} 
                </TableBody> 
                <TableFooter>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      TOTALS ({filteredTransactions.length} transactions)
                    </TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem', color: 'success.main' }}>
                      {(filteredTotals.cardAmount)}
                    </TableCell>
                    <TableCell align="right">-</TableCell>

                    {/* Conditionally show empty cells for conditional columns */}
                    {hasChineseTransactions && <TableCell align="right">-</TableCell>}
                    {hasChineseTransactions && <TableCell align="right">-</TableCell>}
                    
                    {hasChineseTransactions && (
                      <>
                        <TableCell align="right">-</TableCell>
                      </>)}

                        {hasChineseTransactions && <TableCell colSpan={3} align="right">-</TableCell>}
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrencyN(filteredTotals.costPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatCurrencyN(filteredTotals.newCostPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrencyN(filteredTotals.sellingPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      {formatCurrencyN(filteredTotals.commission)}
                    </TableCell>
                    {hasChineseTransactions && (
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        {formatCurrencyN(filteredTotals.newCommission)}
                      </TableCell>
                    )}
                    <TableCell colSpan={3} align="center">-</TableCell>
                  </TableRow>
                </TableFooter>
              </Table> 
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredTransactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                sx={{ borderTop: '1px solid #e0e0e0' }}
              />
            </TableContainer> 
          ) : ( 
            <Paper sx={{ p: 3, textAlign: "center" }}> 
              <Typography variant="body1" color="text.secondary"> 
                No transactions found. 
              </Typography> 
            </Paper> 
          )} 
        </Box> 
      </IonContent> 

      {/* Verification Dialog */}
      <VerificationDialog />

    </IonPage> 
  ); 
}; 

export default CustomerAccountPage_conf_giftcard_crypto_all;